import {
  FormInput,
  FormSelect,
  RadioGroup,
  RadioGroupDataInterface,
  Tooltip,
  copyTextToClipboard,
} from '@systemeio/ui'
import {
  createCommunity,
  removeCommunity,
  updateCommunity,
} from 'modules/commutinies/api/community-api'
import { CommunitiesLoco } from 'modules/commutinies/constants/communities-loco'
import { useCommunities } from 'modules/commutinies/hooks/use-communities'
import {
  CommunityCreateInterface,
  CommunityErrorsInterface,
  CommunityInterface,
} from 'modules/commutinies/types/community-interface'
import { getCommunityUrl, getLoginCommunityUrl } from 'modules/commutinies/utils/get-community-url'
import { NextPage } from 'next'
import React, { useEffect, useState } from 'react'
import { CreateDataType } from 'shared/components/create-modal'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import useUser from 'shared/hooks/use-user'
import useUserDomains from 'shared/hooks/use-user-domains'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { generatePathFromName } from 'shared/utils/path-utils'
import { CopyIcon } from '@systemeio/ui/icons'

const defaultErrors: CommunityErrorsInterface = {
  name: '',
  path: '',
  domainId: '',
  autoPostsApproval: '',
}

const Community: NextPage = () => {
  const { data: communities, mutate } = useCommunities()
  const [name, setName] = useState('')
  const { userDomainsSelect, userDefaultDomain } = useUserDomains()
  const [errors, setErrors] = useState<CommunityErrorsInterface>(defaultErrors)
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const [defaultValues, setDefaultValues] = useState<CommunityCreateInterface>({
    name: '',
    path: '',
    domainId: undefined,
    autoPostsApproval: true,
  })

  const { revalidateModules } = useRevalidateModules()

  useEffect(() => {
    if (userDefaultDomain) {
      setDefaultValues(prev => ({ ...prev, domainId: userDefaultDomain.id }))
    }
  }, [userDefaultDomain])

  const autoApprovalOptions: RadioGroupDataInterface<string>[] = [
    { id: 'enabled', caption: t('dashboard.communities.auto_posts_approval.enabled') },
    { id: 'disabled', caption: t('dashboard.communities.auto_posts_approval.disabled') },
  ]

  const columns: ColumnDefinitionInterface<CommunityInterface>[] = [
    {
      key: 'name',
      header: t('global.title'),
      widthPercent: 25,
      onRender: community => (
        <div className="truncate">
          <a
            target="_blank"
            rel="noreferrer"
            className="primary-link w-full"
            href={getLoginCommunityUrl(community)}
          >
            {community.name}
          </a>
        </div>
      ),
    },
    {
      key: 'path',
      header: t('dashboard.communities.home_page_url'),
      widthPercent: 45,
      onRender: community => (
        <div className="flex w-full gap-3">
          <Tooltip mode="hover" label={t('global.copy')}>
            <button
              onClick={async () => {
                await copyTextToClipboard(getCommunityUrl(community), t('global.copy_clipboard'))
              }}
              className="group outline-hidden"
            >
              <CopyIcon className="group-focus-visible:fill-brand-primary hover:fill-brand-primary h-[16px] w-[16px] cursor-pointer sm:h-[20px] sm:w-[20px]" />
            </button>
          </Tooltip>
          <div className="truncate">{getCommunityUrl(community)}</div>
        </div>
      ),
      onRenderSmall: community => (
        <div className={'flex w-full items-center gap-3'}>
          <span className={'text-text-primary whitespace-nowrap font-bold'}>
            {t('dashboard.communities.home_page_url')}
          </span>
          <div className="flex w-fit gap-3 truncate lg:w-full">
            <div className="truncate">{getCommunityUrl(community)}</div>
            <Tooltip mode="hover" label={t('global.copy')}>
              <button
                onClick={async () => {
                  await copyTextToClipboard(getCommunityUrl(community), t('global.copy_clipboard'))
                }}
                className="group outline-hidden"
              >
                <CopyIcon className="group-focus-visible:fill-brand-primary hover:fill-brand-primary h-[16px] w-[16px] cursor-pointer sm:h-[20px] sm:w-[20px]" />
              </button>
            </Tooltip>
          </div>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: t('global.created_on'),
      widthPercent: 20,
      onRender: community => (
        <span>
          {dateTimeFormat(community.createdAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        </span>
      ),
    },
  ]

  async function onRemoveCommunity(community: CommunityInterface) {
    await removeCommunity(community.id)
    await mutate(data => {
      if (data) {
        return data.filter(item => item.id !== community.id)
      }
    }, false)
    await revalidateModules([RequestModuleEnum.Community])
  }

  const onUpdateCommunity = async (
    community: CommunityInterface,
    newData: CommunityCreateInterface,
  ) => {
    await fetchWithErrors(async () => {
      await updateCommunity(community.id, {
        name: newData.name,
        path: newData.path,
        domainId: newData.domainId,
        autoPostsApproval: newData.autoPostsApproval,
      })
      await mutate(data => {
        if (data) {
          return data.map(item => (item.id === community.id ? { ...item, ...newData } : item))
        }
      }, false)
      await revalidateModules([RequestModuleEnum.Community])
    }, setErrors)
  }

  async function onCreate(data: CreateDataType<CommunityCreateInterface>) {
    await fetchWithErrors(async () => {
      const response = await createCommunity(data)
      await mutate(data => {
        if (data) {
          return [response.data, ...data]
        }
        return [response.data]
      }, false)
      setName('')
      await revalidateModules([RequestModuleEnum.Community])
    }, setErrors)
  }

  const onModalClose = () => setErrors(defaultErrors)

  return (
    <ListingLayout
      counter={communities && { count: communities.length }}
      data={communities && { items: communities, hasMore: false }}
      title={t('dashboard.communities.title_plural')}
      type={'compact'}
      createData={{
        caption: t('dashboard.communities.title'),
        disabled: !communities || !userDomainsSelect,
        onAfterLeave: onModalClose,
        defaultValues: defaultValues,
        requiredFields: ['name', 'domainId', 'path'],
        onCreate: data => onCreate({ ...defaultValues, ...data }),
        creatable: [
          {
            field: 'name',
            onRender: (value, onChange) => (
              <FormInput
                key="community-name-create"
                value={value}
                label={t('global.name')}
                onChange={e => {
                  onChange(e.target.value)
                  setName(e.target.value)
                }}
                maxLength={256}
                error={errors.name}
              />
            ),
          },
          {
            field: 'domainId',
            onRender: (value, onChange) => {
              return (
                <FormSelect
                  key="community-domainId-create"
                  onChange={value => onChange(value)}
                  value={value}
                  label={t('dashboard.communities.domain_name')}
                  data={userDomainsSelect}
                  placeholder={t('dashboard.communities.choose_domain')}
                  error={errors.domainId}
                  withoutCloseIcon
                />
              )
            },
          },
          {
            field: 'path',
            onRender: (value, onChange) => (
              <FormInput
                key="community-path-create"
                value={value}
                label={t('dashboard.communities.path')}
                onChange={e => onChange(e.target.value)}
                onFocus={e => {
                  if (!e.target.value) {
                    onChange(generatePathFromName(name))
                  }
                }}
                maxLength={32}
                error={errors.path}
              />
            ),
          },
          {
            field: 'autoPostsApproval',
            onRender: (value, onChange) => (
              <RadioGroup
                key="community-path-auto-approval"
                label={t('dashboard.communities.auto_posts_approval.label')}
                options={autoApprovalOptions}
                onChange={enabled => onChange(enabled === 'enabled')}
                selected={value ? 'enabled' : 'disabled'}
              />
            ),
          },
        ],
      }}
    >
      <Table
        caption={t('dashboard.communities.title')}
        actions={{
          openLink: [
            {
              openInNew: true,
              name: 'open-community',
              setHref: community => getLoginCommunityUrl(community),
            },
          ],
          settings: {
            onEdit: (community, data) => onUpdateCommunity(community, data),
            onModalClose: onModalClose,
            editable: [
              {
                field: 'name',
                onRender: (value, onChange) => (
                  <FormInput
                    key="community-name-edit"
                    label={t('global.name')}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    error={errors.name}
                  />
                ),
              },
              {
                field: 'path',
                onRender: (value, onChange) => (
                  <FormInput
                    key="community-path-edit"
                    label={t('dashboard.communities.path')}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    error={errors.path}
                  />
                ),
              },
              {
                field: 'domainId',
                onRender: (value, onChange) => {
                  return (
                    <FormSelect
                      key="community-domainName-edit"
                      onChange={value => value && onChange(value)}
                      value={value}
                      label={t('dashboard.communities.domain_name')}
                      data={userDomainsSelect}
                      placeholder={t('dashboard.communities.choose_domain')}
                      withoutCloseIcon
                      error={errors.domainId}
                    />
                  )
                },
              },
              {
                field: 'autoPostsApproval',
                onRender: (value, onChange) => (
                  <RadioGroup
                    key="community-path-edit"
                    label={t('dashboard.communities.auto_posts_approval.label')}
                    options={autoApprovalOptions}
                    onChange={enabled => onChange(enabled === 'enabled')}
                    selected={value ? 'enabled' : 'disabled'}
                  />
                ),
              },
            ],
          },
          remove: {
            onChange: community => onRemoveCommunity(community),
          },
        }}
        data={communities && { items: communities, hasMore: false }}
        columns={columns}
      />
    </ListingLayout>
  )
}
export const getServerSideProps = getBaseServerSideProps(
  CommunitiesLoco,
  'header.top_menu.communities',
)

export default Community
